import { Building2, ChevronRight, SearchX, UserRound } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";

import {
  ORGANIZATION_STATUS_LABEL_KEYS,
  ORGANIZATION_STATUS_PILL,
  ORGANIZATIONS_PAGE_SIZE,
  type OrganizationFeatureDefinition,
} from "../constants/organizationsAdmin.constants";
import type {
  AdminOrganization,
  AdminOrganizationMatchedUser,
  GlobalFeatures,
} from "../types/organizationsAdmin.types";
import { organizationFeatureState } from "../utils/organizationFeatures";

export interface OrganizationsAdminTableProps {
  rows: AdminOrganization[];
  isLoading: boolean;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Opens the organization's panel, where every switch lives. */
  onConfigure: (organization: AdminOrganization) => void;
  /** The features this reader may see. */
  catalog: readonly OrganizationFeatureDefinition[];
  /** The general switches; a feature on for everyone is not repeated on every row. */
  globalFeatures: GlobalFeatures | null;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

export function OrganizationsAdminTable({
  rows,
  isLoading,
  page,
  total,
  onPageChange,
  onConfigure,
  catalog,
  globalFeatures,
  hasActiveFilters,
  onClearFilters,
}: OrganizationsAdminTableProps) {
  const { t } = useTranslations("organizations");
  const countryLabel = useCountryLabel();

  const columns = useMemo<ColocaDataTableColumn<AdminOrganization>[]>(
    () => [
      {
        id: "name",
        header: t("col_name"),
        cellClassName: "max-w-80",
        cell: (row) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-foreground" title={row.name}>
              {row.name}
            </span>
            <span className="block truncate text-xs text-muted-foreground" title={row.email}>
              {row.email}
            </span>
            <MatchedUsers users={row.matched_users} />
          </div>
        ),
      },
      {
        id: "country",
        header: t("col_country"),
        cell: (row) => (
          <span className="text-sm text-muted-foreground">
            {row.country ? countryLabel(row.country, row.country) : t("value_none")}
          </span>
        ),
      },
      {
        id: "status",
        header: t("col_status"),
        cell: (row) => (
          <ColocaStatusPill variant={ORGANIZATION_STATUS_PILL[row.status]} size="sm" withDot>
            {t(ORGANIZATION_STATUS_LABEL_KEYS[row.status])}
          </ColocaStatusPill>
        ),
      },
      {
        id: "features",
        header: t("col_features"),
        cell: (row) => (
          <OrganizationFeatureChips organization={row} catalog={catalog} globalFeatures={globalFeatures} />
        ),
      },
      {
        id: "configure",
        header: <span className="sr-only">{t("action_configure")}</span>,
        headerClassName: "w-12",
        cell: (row) => (
          <ColocaIconButton
            icon={ChevronRight}
            variant="ghost"
            size="xs"
            aria-label={t("configure_aria", { name: row.name })}
            onClick={() => onConfigure(row)}
          />
        ),
      },
    ],
    [catalog, countryLabel, globalFeatures, onConfigure, t],
  );

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      loading={isLoading}
      skeletonRows={8}
      minWidth={900}
      onRowClick={onConfigure}
      empty={
        hasActiveFilters ? (
          <ColocaEmpty
            icon={SearchX}
            title={t("empty_filtered_title")}
            description={t("empty_filtered_description")}
            surface="card"
          >
            <ColocaButton variant="default" size="sm" onClick={onClearFilters}>
              {t("action_clear_filters")}
            </ColocaButton>
          </ColocaEmpty>
        ) : (
          <ColocaEmpty
            icon={Building2}
            title={t("empty_title")}
            description={t("empty_description")}
            surface="card"
          />
        )
      }
      footer={
        total > ORGANIZATIONS_PAGE_SIZE ? (
          <ColocaPagination
            page={page}
            perPage={ORGANIZATIONS_PAGE_SIZE}
            total={total}
            withSeparator={false}
            size="sm"
            onPageChange={onPageChange}
          />
        ) : undefined
      }
    />
  );
}

/** Why a searched row is there when the term was a person's name or email, not the organization's. */
function MatchedUsers({ users }: { users?: AdminOrganizationMatchedUser[] }) {
  const { t } = useTranslations("organizations");
  const first = users?.[0];
  if (!first) return null;
  const label = t("matched_user", { name: first.full_name, email: first.email });
  return (
    <span className="mt-1 flex min-w-0 items-center gap-1 text-xs font-medium text-primary" title={label}>
      <UserRound className="size-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
      {users.length > 1 && (
        <span className="shrink-0 text-muted-foreground">
          {t("matched_users_more", { count: users.length - 1 })}
        </span>
      )}
    </span>
  );
}

/** Only what this organization has on its own: a feature on for everyone would repeat on every row. */
function OrganizationFeatureChips({
  organization,
  catalog,
  globalFeatures,
}: {
  organization: AdminOrganization;
  catalog: readonly OrganizationFeatureDefinition[];
  globalFeatures: GlobalFeatures | null;
}) {
  const { t } = useTranslations("organizations");
  const own = catalog.filter(
    (definition) => organizationFeatureState(organization, definition, globalFeatures) === "on",
  );
  if (own.length === 0) {
    const anyGlobal = catalog.some(
      (definition) => definition.global && globalFeatures?.[definition.global] === "on_for_all",
    );
    return (
      <span className="text-xs text-muted-foreground">
        {anyGlobal ? t("features_only_global") : t("features_none")}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {own.map((definition) => (
        <ColocaBadge key={definition.feature} variant="primary" icon={definition.icon}>
          {t(definition.labelKey)}
        </ColocaBadge>
      ))}
    </div>
  );
}
